import {
  Box, Grid,
} from '@mui/material';
import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import HeaderMenuSettingsModal from '../shared/HeaderMenuSettingsModal';
import BlockData from '../../../../models/BlockData';
import BlockProps from '../../../../models/BlockProps';
import LinkData from '../../../../models/LinkData';
import BlockHeaderWithLogoData from './BlockHeaderWithLogoData';
import CloseButton from '../../../Buttons/Close';
import Utils from '../../../../utils/Utils';
import ImageModal from '../../../Modals/ImageModal/ImageModal';
import ImageComponent from '../../../ImageComponent';
import { ImagesContext } from '../../../../contexts/ImagesContext';
import styles from './BlockHeaderWithLogo.module.css';
import LogoHeaderContactInfo from './components/LogoHeaderContactInfo';
import LogoHeaderButtons from './components/LogoHeaderButtons';
import HamburgerMenuButton from '../../../Buttons/HamburgerMenuButton';
import HeaderMenu from '../shared/HeaderMenu';
import useResponsive from '../../../../hooks/UseResponsive';

export interface BlockHeaderWithLogoProps extends BlockProps {
  blockData: BlockData[]
}

function BlockHeaderWithLogo({
  data, isEditing, isTemplate, editHandler, blockData,
}: BlockHeaderWithLogoProps) {
  const props = (data.data as BlockHeaderWithLogoData);
  const { images } = useContext(ImagesContext);

  const [open, setOpen] = useState(false);
  const hamburgerCheckbox = useRef(null);

  useResponsive({
    items: [
      {
        wrapperId: 'logoButtonsWrapper',
        targetId: 'logoButtonsHamburgerMenu',
        queries: [
          { size: 0, className: styles.flex },
          { size: 344, className: styles.hidden },
        ],
      },
      {
        wrapperId: 'logoButtonsWrapper',
        targetId: 'logoButtonsHamburgerMenuItems',
        queries: [
          { size: 0, className: styles.flex },
          { size: 344, className: styles.hidden },
        ],
      },
      {
        wrapperId: 'logoButtonsWrapper',
        targetId: 'logoButtonsMenu',
        queries: [
          { size: 0, className: styles.hidden },
          { size: 344, className: styles.flex },
        ],
      },
      {
        wrapperId: 'logoButtonsWrapper',
        targetId: 'logoButtonsInfo',
        queries: [
          { size: 0, className: styles.hidden },
          { size: 344, className: styles.flex },
        ],
      },
    ],
  });

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  const contactInfo = useMemo(() => (
    <LogoHeaderContactInfo
      contact={props.contact}
      contactInfo={props.contactInfo}
      isEditing={isEditing}
      isTemplate={isTemplate}
      onRemoveContact={() => {
        props.contact.value = undefined;
        editHandler(data.id, props);
      }}
      onSaveContact={(text) => {
        props.contact.value = text;
        editHandler(data.id, props);
      }}
      onRemoveContactInfo={() => {
        props.contactInfo.value = undefined;
        editHandler(data.id, props);
      }}
      onSaveContactInfo={(text) => {
        props.contactInfo.value = text;
        editHandler(data.id, props);
      }}
    />
  ), [data.id, editHandler, isEditing, isTemplate, props]);

  const buttons = useMemo(() => (
    <LogoHeaderButtons
      leftButton={props.leftButton}
      rightButton={props.rightButton}
      isEditing={isEditing}
      isTemplate={isTemplate}
      blockData={blockData}
      onRemoveLeftButton={() => {
        props.leftButton = undefined;
        editHandler(data.id, props);
      }}
      onSaveLeftButton={(item) => {
        props.leftButton = item;
        editHandler(data.id, props);
      }}
      onRemoveRightButton={() => {
        props.rightButton = undefined;
        editHandler(data.id, props);
      }}
      onSaveRightButton={(item) => {
        props.rightButton = item;
        editHandler(data.id, props);
      }}
    />
  ), [blockData, data.id, editHandler, isEditing, isTemplate, props]);

  const contactInfoAndButtons = useMemo(() => (
    <Grid id="logoButtonsInfo" container justifyContent="end" alignItems="center">
      <Grid item xs="auto">
        {contactInfo}
      </Grid>

      {props.leftButton || props.rightButton ? (
        <Grid item xs="auto" sx={{ marginLeft: 1.5 }}>
          {buttons}
        </Grid>
      ) : null}
    </Grid>
  ), [buttons, contactInfo, props.leftButton, props.rightButton]);

  const hamburgerButton = useMemo(() => (
    <Grid id="logoButtonsHamburgerMenu" item xs="auto">
      {isEditing || isTemplate ? (
        <HamburgerMenuButton />
      ) : (
        <label htmlFor={styles['show-menu']}>
          <HamburgerMenuButton />
        </label>
      )}
    </Grid>
  ), [isEditing, isTemplate]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid id="logoButtonsMenu" container sx={{ p: 1.5, paddingTop: 0 }}>
          <HeaderMenu
            menus={props.menus}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }

    return [];
  }, [props.menus, isEditing, handleMenuDelete, handleOpen]);

  const hamburgerMenu = useMemo(() => (
    <Grid
      id="logoButtonsHamburgerMenuItems"
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      sx={{
        p: 1.5,
        backgroundColor: '#f5f7fb',
      }}
    >

      <Grid item xs={12} sx={{ width: '100%' }}>
        {contactInfo}
      </Grid>

      {props.menus && props.menus?.length > 0 ? (
        <Grid item xs={12} sx={{ width: '100%' }}>
          <HeaderMenu
            menus={props.menus}
            isEditing={isEditing}
            direction="column"
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      ) : null}

      <Grid item xs={12} sx={{ width: '100%' }}>
        {buttons}
      </Grid>

    </Grid>
  ), [buttons, contactInfo, handleMenuDelete, handleOpen, isEditing, props.menus]);

  useEffect(() => {
    if (isEditing) {
      hamburgerCheckbox.current.checked = false;
    }
  }, [isEditing]);

  return (
    <>
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        className={styles.header}
      >
        <Grid container justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
          {props.logo ? (
            <Grid item xs={6} sx={Utils.hoverBorder(isEditing)}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.logo = undefined;
                  editHandler(data.id, props);
                }}
              />
              <Box sx={{ height: '110px' }}>
                <ImageModal
                  disabled={isTemplate || !isEditing}
                  images={images}
                  handleOnConfirm={(base64image: string) => {
                    props.logo.value = base64image;
                    editHandler(data.id, props);
                  }}
                >
                  <ImageComponent src={props.logo.value} />
                </ImageModal>
              </Box>
            </Grid>
          ) : (
            <Grid item />
          )}

          <Grid item xs={6}>
            <Box
              id="logoButtonsWrapper"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {hamburgerButton}
              {contactInfoAndButtons}
            </Box>
          </Grid>

        </Grid>

        {props.menus && (
          <>
            {menuItems}
          </>
        )}

        <input type="checkbox" id={styles['show-menu']} ref={hamburgerCheckbox} />
        <Box id={styles['hamburger-menu']} sx={{ width: '100%' }}>
          {hamburgerMenu}
          {!isEditing && (
            <Box className={styles.fadeDiv} />
          )}
        </Box>

        <HeaderMenuSettingsModal
          menuItems={props.menus ?? []}
          open={open}
          closeHandler={(newItems?: LinkData[]) => {
            if (newItems) {
              props.menus = newItems;
              editHandler(data.id, props);
            }
            handleClose();
          }}
          blockData={blockData}
        />

      </Grid>
      {!isEditing && (
        <Box className={styles.fadeDiv} />
      )}
    </>
  );
}

export default BlockHeaderWithLogo;
